import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BLOG_POSTS } from "@/blog/data";
import { SHOW_SISTEMA_LUCENTIA } from "@/config/features";
import { Logo } from "@/components/brand/Logo";
import { AuditButton, FreeAuditButton } from "@/components/ui/Cta";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { scrollToSectionIdWhenReady } from "@/utils/smoothScroll";
import { CartButton } from "@/components/store/CartButton";
import { LanguageToggle } from "./LanguageToggle";

type NavItem = { href: string; labelKey: string; mobileOnly?: boolean };

/* The blog joins the menu once it has articles to show. Inicio is mobile-only:
   the desktop bar already uses the logo for home, and a seventh item does not fit. */
const NAV_ITEMS: NavItem[] = [
  { href: "/", labelKey: "home", mobileOnly: true },
  ...(SHOW_SISTEMA_LUCENTIA ? [{ href: "/metodo", labelKey: "methodology" }] : []),
  { href: "/#servicios", labelKey: "services" },
  { href: "/precios", labelKey: "pricing" },
  ...(BLOG_POSTS.length ? [{ href: "/blog", labelKey: "blog" }] : []),
  { href: "/recursos", labelKey: "resources" },
  { href: "/#sobre-mi", labelKey: "about" },
  { href: "/#contacto", labelKey: "contact" },
];

const SERVICE_PATHS = new Set(["/pagina-web", "/gestion-instagram", "/asesoria-instagram"]);

function isActive(href: string, pathname: string, homeSection: string | null) {
  if (href === "/") return pathname === "/" && homeSection === null;
  if (href.startsWith("/#")) {
    const id = href.slice(2);
    if (pathname === "/") return homeSection === id;
    return id === "servicios" && SERVICE_PATHS.has(pathname);
  }
  return (
    pathname === href ||
    (href === "/blog" && pathname.startsWith("/blog/")) ||
    (href === "/recursos" && pathname.startsWith("/recursos/"))
  );
}

/** Tracks which home section is under the header, for nav highlighting. */
function useHomeSection(enabled: boolean) {
  const [section, setSection] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) {
      setSection(null);
      return;
    }
    const ids = ["servicios", "sobre-mi", "contacto"];
    const seen = new Map<string, boolean>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => seen.set(e.target.id, e.isIntersecting));
        setSection(ids.find((id) => seen.get(id)) ?? null);
      },
      { rootMargin: "-35% 0px -55% 0px" },
    );
    const observeAll = () => ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    observeAll();
    const retry = window.setTimeout(observeAll, 800);
    return () => {
      window.clearTimeout(retry);
      io.disconnect();
    };
  }, [enabled]);
  return section;
}

function NavLinkItem({
  item,
  className,
  activeClassName,
  homeSection,
  onNavigate,
}: {
  item: NavItem;
  className: string;
  activeClassName: string;
  homeSection: string | null;
  onNavigate?: () => void;
}) {
  const { t } = useAppTranslation("nav");
  const location = useLocation();
  const navigate = useNavigate();
  const active = isActive(item.href, location.pathname, homeSection);
  const cls = `${className} ${active ? activeClassName : ""}`;

  if (item.href === "/") {
    return (
      <a
        href="/"
        aria-current={active ? "page" : undefined}
        className={cls}
        onClick={(e) => {
          e.preventDefault();
          onNavigate?.();
          if (location.pathname !== "/") {
            navigate("/");
            return;
          }
          window.history.replaceState(null, "", "/");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      >
        {t(item.labelKey)}
      </a>
    );
  }

  if (item.href.startsWith("/#")) {
    const id = item.href.slice(2);
    return (
      <a
        href={item.href}
        aria-current={active ? "true" : undefined}
        className={cls}
        onClick={(e) => {
          e.preventDefault();
          onNavigate?.();
          if (location.pathname !== "/") {
            navigate({ pathname: "/", hash: `#${id}` });
            return;
          }
          window.history.replaceState(null, "", `#${id}`);
          scrollToSectionIdWhenReady(id);
        }}
      >
        {t(item.labelKey)}
      </a>
    );
  }

  return (
    <Link to={item.href} aria-current={active ? "page" : undefined} className={cls} onClick={onNavigate}>
      {t(item.labelKey)}
    </Link>
  );
}

export function Header() {
  const { t: tNav } = useAppTranslation("nav");
  const { t: tCommon } = useAppTranslation("common");
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const panelId = useId();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const homeSection = useHomeSection(location.pathname === "/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function goHome(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const solid = scrolled || open;

  return (
    <header
      className={`fixed inset-x-0 top-[var(--banner-h,0px)] z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        open
          ? "border-b border-brand-primary/8 bg-white shadow-[0_8px_30px_-20px_rgb(26_60_94/0.35)]"
          : solid
            ? "border-b border-brand-primary/8 bg-white/92 shadow-[0_8px_30px_-20px_rgb(26_60_94/0.35)] backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-18 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:h-21 lg:px-8">
        <a href="/" onClick={goHome} aria-label={tCommon("homeLogoAria")} className="shrink-0 rounded-lg transition-opacity duration-300 hover:opacity-80">
          <Logo layout="horizontal" priority className="w-[116px] sm:w-[156px]" alt="" />
        </a>

        <nav aria-label={tNav("mainNav")} className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV_ITEMS.filter((item) => !item.mobileOnly).map((item) => (
              <li key={item.href}>
                <NavLinkItem
                  item={item}
                  homeSection={homeSection}
                  className="relative rounded-full px-2.5 py-2 text-sm font-medium text-ink-soft transition-colors hover:text-brand-primary after:absolute after:inset-x-2.5 after:-bottom-0.5 after:h-px after:origin-left after:scale-x-0 after:bg-brand-secondary after:transition-transform after:duration-300 hover:after:scale-x-100 xl:px-3.5 xl:after:inset-x-3.5"
                  activeClassName="!text-brand-primary after:!scale-x-100"
                />
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden lg:block">
            <LanguageToggle />
          </div>
          <CartButton onStorePage={location.pathname === "/recursos" || location.pathname.startsWith("/recursos/")} />
          <FreeAuditButton
            location="header"
            variant="primary"
            size="md"
            label={
              <>
                <span className="sm:hidden">{tCommon("requestAuditTiny")}</span>
                <span className="hidden sm:inline">{tCommon("requestAudit")}</span>
              </>
            }
            className="!min-h-10 whitespace-nowrap !px-4 text-sm sm:!min-h-11 sm:!px-5 sm:text-sm [&_svg]:hidden sm:[&_svg]:block"
          />
          <button
            ref={menuButtonRef}
            type="button"
            className="grid size-11 place-items-center rounded-full text-brand-primary transition-colors hover:bg-teal-soft lg:hidden"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={open ? tNav("closeMenu") : tNav("openMenu")}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X aria-hidden="true" className="size-6" /> : <Menu aria-hidden="true" className="size-6" />}
          </button>
        </div>
      </div>

      <div aria-hidden="true" className="scroll-progress absolute inset-x-0 bottom-0 h-px bg-brand-secondary" />

      {open ? (
        <nav
          id={panelId}
          aria-label={tNav("mobileNav")}
          className="fixed inset-x-0 bottom-0 top-[calc(var(--banner-h,0px)+4.5rem)] overflow-y-auto bg-white lg:hidden"
        >
          <div className="light-pool flex min-h-full flex-col px-6 pb-10 pt-6 [--pool-x:90%] [--pool-y:0%]">
            <ul className="flex flex-col">
              {NAV_ITEMS.map((item) => (
                <li key={item.href} className="border-b border-brand-primary/8">
                  <NavLinkItem
                    item={item}
                    homeSection={homeSection}
                    onNavigate={() => setOpen(false)}
                    className="flex min-h-16 items-center font-display text-3xl font-medium text-brand-primary"
                    activeClassName="text-teal-strong"
                  />
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3">
              <FreeAuditButton location="menu_mobile" className="w-full" />
              <AuditButton location="menu_mobile" className="w-full" />
            </div>
            <div className="mt-8 flex justify-center">
              <LanguageToggle variant="mobile" />
            </div>
          </div>
        </nav>
      ) : null}
    </header>
  );
}
